import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Search, RotateCcw, Save, ArrowLeft, Loader2, CheckCircle2, AlertCircle,
  Globe2, FileText, Languages, RefreshCw, X, CloudDownload,
} from "lucide-react";
import AdminAuthGate, { adminHeaders } from "@/components/admin/AdminAuthGate";
import { resetSlotCaches } from "@/lib/slotCache";
import { devLog } from "../lib/devLog";
import { KNOWN_PAGES, KNOWN_PATHS, KNOWN_META } from "@/lib/knownPages";

const API = process.env.REACT_APP_BACKEND_URL || "";
const LANGS = ["es", "en", "fr"];
const LANG_LABEL = { es: "Español", en: "English", fr: "Français" };
const LANG_FLAG = { es: "ES", en: "EN", fr: "FR" };

// ── Page grouping ────────────────────────────────────────────────────────
// Every row's `page` should match one of the KNOWN_PAGES entries. Legacy
// slots stored before the crawler recorded `page` fall back to a
// prefix-based bucket so they don't get lost.

const groupOfRow = (row) => {
  if (row.page) return row.page;
  // Fallback: derive from slot id namespace.
  const slot = row.slot_id || "";
  if (slot.startsWith("text.root.")) return "(unsorted)";
  if (slot.startsWith("text.")) {
    const rest = slot.slice(5);
    const dot = rest.indexOf(".");
    const seg = dot > 0 ? rest.slice(0, dot) : rest;
    return seg.replace(/^_+/, "/").replace(/_/g, "/");
  }
  if (slot.startsWith("audio_guide.")) return "(audio guides)";
  return "(other)";
};

const groupLabel = (key) => {
  if (key === "(unsorted)") return { label: "Unsorted (legacy slots)", emoji: "📦" };
  if (key === "(audio guides)") return { label: "Audio guides", emoji: "🎧" };
  if (key === "(other)") return { label: "Other", emoji: "•" };
  if (KNOWN_META.has(key)) {
    const meta = KNOWN_META.get(key);
    return { label: meta.label, emoji: meta.emoji };
  }
  return { label: key, emoji: "•" };
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

// ── Page component ──────────────────────────────────────────────────────
const AdminTextInner = () => {
  const [rows, setRows] = useState([]);          // [{slot_id, values, page, source_lang, updated_at}]
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState("__all");
  const [drafts, setDrafts] = useState({});      // { slot_id: { es, en, fr } }
  const [busy, setBusy] = useState({});          // { slot_id: bool }
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  // Pagination — we have ~18k slots in the DB. Rendering them all explodes
  // the browser. Show a window of `pageSize` at a time per active group.
  const PAGE_SIZE = 30;
  const [visible, setVisible] = useState(PAGE_SIZE);
  // Reset the visible window whenever the filter context changes.
  useEffect(() => { setVisible(PAGE_SIZE); }, [activeGroup, query]);

  // ── Full-site sync job ───────────────────────────────────────────────
  const [sync, setSync] = useState({ visible: false, running: false, tail: [], startedAt: null, finishedAt: null, slotCount: null });
  const syncPollRef = useRef(null);

  const pollSyncOnce = async () => {
    try {
      const r = await fetch(`${API}/api/text_slots/sync/status`, {
      credentials: "include", cache: "no-store" });
      const j = await r.json();
      setSync((cur) => ({
        ...cur,
        running: !!j.running,
        tail: j.tail || [],
        startedAt: j.started_at || cur.startedAt,
        finishedAt: j.finished_at || cur.finishedAt,
        slotCount: j.slot_count ?? cur.slotCount,
      }));
      return !!j.running;
    } catch {
      return false;
    }
  };

  const startSyncPolling = () => {
    if (syncPollRef.current) window.clearInterval(syncPollRef.current);
    syncPollRef.current = window.setInterval(async () => {
      const stillRunning = await pollSyncOnce();
      if (!stillRunning) {
        window.clearInterval(syncPollRef.current);
        syncPollRef.current = null;
        showToast("ok", "Sync finished — reloading slots…");
        // Pull the freshly populated list.
        load();
      }
    }, 2500);
  };

  useEffect(() => () => {
    if (syncPollRef.current) window.clearInterval(syncPollRef.current);
  }, []);

  // On mount, see if a sync is already running (someone else triggered it).
  useEffect(() => { (async () => {
    try {
      const r = await fetch(`${API}/api/text_slots/sync/status`, {
      credentials: "include", cache: "no-store" });
      const j = await r.json();
      if (j.running) {
        setSync({ visible: true, running: true, tail: j.tail || [], startedAt: j.started_at, finishedAt: null, slotCount: j.slot_count });
        startSyncPolling();
      }
    } catch (err) { devLog.debug("[AdminText] sync status probe failed", err); /* noop */ }
  })(); }, []);

  // ── Bulk-translate job ──────────────────────────────────────────────
  // Fills every empty ES / EN / FR value on rows that already have at
  // least one language populated. Runs in the backend as an asyncio
  // task fanned out to Claude Haiku with a concurrency cap. Two entry
  // points: a global button in the top toolbar (whole site) and a
  // per-page button inside the group view (scoped to the current page).
  const [translate, setTranslate] = useState({
    visible: false, running: false, page: null,
    total: 0, done: 0, translated: 0, failed: 0,
    currentSlot: null, startedAt: null, finishedAt: null, error: null,
  });
  const translatePollRef = useRef(null);

  const pollTranslateOnce = async () => {
    try {
      const r = await fetch(`${API}/api/text_slots/bulk-translate/status`, {
        credentials: "include", cache: "no-store",
      });
      const j = await r.json();
      setTranslate((cur) => ({
        ...cur,
        running: !!j.running,
        page: j.page ?? cur.page,
        total: j.total ?? cur.total,
        done: j.done ?? cur.done,
        translated: j.translated ?? cur.translated,
        failed: j.failed ?? cur.failed,
        currentSlot: j.current_slot ?? cur.currentSlot,
        startedAt: j.started_at || cur.startedAt,
        finishedAt: j.finished_at || cur.finishedAt,
        error: j.error || null,
      }));
      return !!j.running;
    } catch {
      return false;
    }
  };

  const startTranslatePolling = () => {
    if (translatePollRef.current) window.clearInterval(translatePollRef.current);
    translatePollRef.current = window.setInterval(async () => {
      const stillRunning = await pollTranslateOnce();
      if (!stillRunning) {
        window.clearInterval(translatePollRef.current);
        translatePollRef.current = null;
        showToast("ok", "Bulk translation finished — reloading slots…");
        load();
      }
    }, 2000);
  };

  useEffect(() => () => {
    if (translatePollRef.current) window.clearInterval(translatePollRef.current);
  }, []);

  // On mount, see if a bulk-translate is already running.
  useEffect(() => { (async () => {
    try {
      const r = await fetch(`${API}/api/text_slots/bulk-translate/status`, {
        credentials: "include", cache: "no-store",
      });
      const j = await r.json();
      if (j.running) {
        setTranslate({
          visible: true, running: true, page: j.page,
          total: j.total || 0, done: j.done || 0, translated: j.translated || 0, failed: j.failed || 0,
          currentSlot: j.current_slot, startedAt: j.started_at, finishedAt: null, error: null,
        });
        startTranslatePolling();
      }
    } catch (err) { devLog.debug("[AdminText] translate status probe failed", err); /* noop */ }
  })(); }, []);

  const startBulkTranslate = async (pageFilter) => {
    // Pretty label for the confirm + drawer title
    const scope = pageFilter ? `page ${pageFilter}` : "the ENTIRE site";
    const msg = pageFilter
      ? `Fill every empty ES / EN / FR value on ${pageFilter} using Claude Haiku?\n\nAlready-filled languages are NEVER overwritten. Runs ~2s per missing value.`
      : `Fill every empty ES / EN / FR value across the ENTIRE site using Claude Haiku?\n\nAlready-filled languages are NEVER overwritten. On a big site with many gaps this can take several minutes.`;
    if (!window.confirm(msg)) return;
    setTranslate((cur) => ({
      ...cur, visible: true, running: true, page: pageFilter || null,
      total: 0, done: 0, translated: 0, failed: 0, currentSlot: null,
      startedAt: null, finishedAt: null, error: null,
    }));
    try {
      const r = await fetch(`${API}/api/text_slots/bulk-translate`, {
        credentials: "include", method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(pageFilter ? { page: pageFilter } : {}),
      });
      const j = await r.json();
      if (j.already_running) {
        showToast("ok", "A bulk translation is already in progress.");
      }
      setTranslate((cur) => ({
        ...cur, running: !!j.running, startedAt: j.started_at || cur.startedAt,
      }));
      startTranslatePolling();
      showToast("ok", `Bulk translation started for ${scope}.`);
    } catch (e) {
      showToast("error", e.message || "Failed to start bulk translation");
      setTranslate((cur) => ({ ...cur, running: false, error: e.message || String(e) }));
    }
  };

  const startSync = async () => {
    const ok = window.confirm(
      "This will scan every page of the site in EN / FR / ES, register every visible text string into the CMS, " +
      "and replicate to production. Takes ~3–5 minutes. Continue?"
    );
    if (!ok) return;
    setSync((cur) => ({ ...cur, visible: true, running: true, tail: ["Starting Playwright crawler…"] }));
    try {
      const r = await fetch(`${API}/api/text_slots/sync`, {
      credentials: "include", method: "POST" });
      const j = await r.json();
      setSync((cur) => ({
        ...cur,
        running: !!j.running,
        startedAt: j.started_at || cur.startedAt,
      }));
      startSyncPolling();
    } catch (e) {
      showToast("error", e.message || "Failed to start sync");
      setSync((cur) => ({ ...cur, running: false }));
    }
  };

  // ── Pull from production ────────────────────────────────────────────
  // Mirrors EVERY CMS collection (text_slots, image_slots, media_assets,
  // media_folders, slot_files, galleries, pricing, testimonials,
  // heritage, regions, did_you_know, directions, rooms_cms) from
  // https://visitxaluca.com into this env's MongoDB so preview becomes
  // a byte-for-byte copy of the live site's CMS.
  const [pulling, setPulling] = useState(false);

  const pullFromProduction = async () => {
    const ok = window.confirm(
      "Mirror every CMS collection from https://visitxaluca.com into this environment?\n\n" +
      "This wipes and replaces all local CMS collections (texts, images, media, " +
      "galleries, pricing, testimonials, heritage, regions, directions, " +
      "did-you-know, rooms_cms) with their production counterparts. " +
      "Customer data (bookings, contacts, chats) is NOT touched.\n\n" +
      "Requires the production deployment to have the latest /api/admin/db/pull " +
      "endpoint. If you haven't redeployed since this feature was added, redeploy first."
    );
    if (!ok) return;
    setPulling(true);
    try {
      const r = await fetch(`${API}/api/admin/db/pull`, {
      credentials: "include",
        method: "POST",
        headers: {
          ...adminHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          source_url: "https://visitxaluca.com",
          replace: true,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      const results = j.results || [];
      const ok_ = results.filter((x) => x.status === "ok");
      const failed = results.filter((x) => x.status === "failed");
      const skipped = results.filter((x) => x.status === "skipped");
      const totalUpserted = ok_.reduce((s, x) => s + (x.upserted || 0), 0);
      let msg = `Pulled ${totalUpserted.toLocaleString()} documents across ${ok_.length} collection${ok_.length === 1 ? "" : "s"}`;
      if (skipped.length) msg += ` · ${skipped.length} skipped (endpoint missing)`;
      if (failed.length) msg += ` · ${failed.length} failed`;
      showToast(failed.length ? "error" : "ok", msg);
      // Detailed log in console for the operator
      devLog.debug("[AdminText] DB pull results:", j);

      // CRITICAL: the React app caches text + image slot data both in
      // memory (slotCache.js) and in localStorage. Without wiping
      // them the user navigates to / and still sees pre-mirror image
      // URLs even though MongoDB is now in sync. We:
      //   1. Reset every in-memory + localStorage layer
      //   2. Hard-reload after a short delay so any in-flight state
      //      (React tree, sticky `imgCache.ready=true`, debounced
      //      `persistToLocalStorage` writes) is fully discarded.
      if (!failed.length) {
        resetSlotCaches();
        window.setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        // Partial failure — still refresh the directory but don't
        // reload so the operator can read the failed-collections list.
        await load();
      }
    } catch (e) {
      showToast("error", e.message || "Failed to pull from production");
    } finally {
      setPulling(false);
    }
  };

  // ── Load ─────────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/text_slots?detailed=1`, {
      credentials: "include", cache: "no-store" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      const next = (j.slots || []).map((row) => ({
        slot_id: row.slot_id,
        values: row.values || {},
        page: row.page || null,
        source_lang: row.source_lang || null,
        updated_at: row.updated_at || null,
      }));
      setRows(next);
    } catch (e) {
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-trigger the mirror flow when arriving from the sidebar
  // shortcut (`/admin/text?mirror=auto`). We strip the param from the
  // URL immediately so a refresh doesn't keep firing the dialog.
  // We wait one tick to ensure pullFromProduction is closed over the
  // freshest state (it reads `pulling` via setState callback).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("mirror") !== "auto") return;
    // Clean the URL so reloads behave normally.
    params.delete("mirror");
    const cleanQuery = params.toString();
    const cleanUrl =
      window.location.pathname + (cleanQuery ? `?${cleanQuery}` : "") + window.location.hash;
    window.history.replaceState(null, "", cleanUrl);
    // Fire after the next paint so the page is interactive when the
    // browser-native confirm() dialog opens.
    const id = window.setTimeout(() => { pullFromProduction(); }, 250);
    return () => window.clearTimeout(id);
  }, []);

  // ── Groups + filtering ───────────────────────────────────────────────
  // Merge DB-derived counts with the full KNOWN_PAGES list so every real
  // page in the app appears here — even pages nobody has scanned or
  // edited yet. Those show up with count=0 and a "Not scanned yet" hint
  // in the card, guiding the operator toward the Re-scan button.
  const groups = useMemo(() => {
    const counts = new Map();
    // Seed every known page at 0
    for (const p of KNOWN_PAGES) counts.set(p.path, 0);
    // Add DB rows
    for (const r of rows) {
      const g = groupOfRow(r);
      counts.set(g, (counts.get(g) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => {
        // Order: known pages first (grouped by category, then path),
        // then legacy catch-all buckets last.
        const sentinel = (k) => (k.startsWith("(") ? 2 : (KNOWN_PATHS.has(k) ? 0 : 1));
        const sa = sentinel(a[0]);
        const sb = sentinel(b[0]);
        if (sa !== sb) return sa - sb;
        return a[0].localeCompare(b[0]);
      });
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter((r) => activeGroup === "__all" || groupOfRow(r) === activeGroup)
      .filter((r) => {
        if (!q) return true;
        const hay = [
          r.slot_id, r.page || "",
          r.values?.en || "", r.values?.fr || "", r.values?.es || "",
        ].join(" ").toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const ta = a.updated_at ? Date.parse(a.updated_at) : 0;
        const tb = b.updated_at ? Date.parse(b.updated_at) : 0;
        return tb - ta;
      });
  }, [rows, query, activeGroup]);

  // ── Draft helpers ────────────────────────────────────────────────────
  const draftOf = (row) => drafts[row.slot_id] || row.values || {};

  const setDraftField = (slotId, lang, text) => {
    setDrafts((cur) => {
      const prev = cur[slotId] || rows.find((r) => r.slot_id === slotId)?.values || {};
      return { ...cur, [slotId]: { ...prev, [lang]: text } };
    });
  };

  const dirtyLangs = (row) => {
    const d = drafts[row.slot_id];
    if (!d) return [];
    const out = [];
    for (const l of LANGS) {
      if (typeof d[l] === "string" && d[l] !== (row.values?.[l] || "")) out.push(l);
    }
    return out;
  };

  const isDirty = (row) => dirtyLangs(row).length > 0;

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3200);
  };

  // ── Save ─────────────────────────────────────────────────────────────
  // One call per dirty language (preserves auto-translation: backend will
  // fan out to the other two languages for each save).
  const saveRow = async (row) => {
    const dirty = dirtyLangs(row);
    if (!dirty.length) return;
    setBusy((b) => ({ ...b, [row.slot_id]: true }));
    const d = drafts[row.slot_id] || {};
    let merged = { ...row.values };
    let lastUpdated = row.updated_at;
    try {
      for (const lang of dirty) {
        const text = d[lang] || "";
        const r = await fetch(`${API}/api/text_slots/${encodeURIComponent(row.slot_id)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ values: { [lang]: text }, page: row.page || null }),
          cache: "no-store",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        merged = j.values || merged;
        lastUpdated = j.updated_at || lastUpdated;
      }
      // Apply the synchronous server response (translations land async in ~2s).
      setRows((cur) => cur.map((r) => (
        r.slot_id === row.slot_id
          ? { ...r, values: merged, updated_at: lastUpdated }
          : r
      )));
      // Clear the draft entry for this slot.
      setDrafts((cur) => { const n = { ...cur }; delete n[row.slot_id]; return n; });
      showToast("ok", `Saved · auto-translating the other languages…`);
      // Refresh after 2.5s so we pick up the auto-translated siblings.
      window.setTimeout(load, 2800);
    } catch (e) {
      showToast("error", e.message || "Save failed");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[row.slot_id]; return n; });
    }
  };

  const resetRow = (row) => {
    setDrafts((cur) => { const n = { ...cur }; delete n[row.slot_id]; return n; });
  };

  // ── Per-row translate ─────────────────────────────────────────────────
  // Fills the missing es/en/fr cells on ONE slot using Claude Haiku.
  // Synchronous — the operator wants to see the freshly translated
  // cells appear right away in the same row they clicked. Never
  // overwrites already-filled languages (server-side merge).
  const translateRow = async (row) => {
    const values = row.values || {};
    const filled = ["es", "en", "fr"].filter((l) => (values[l] || "").trim());
    if (filled.length === 0) {
      showToast("error", "Nothing to translate from — this slot is empty in every language.");
      return;
    }
    setBusy((b) => ({ ...b, [row.slot_id]: true }));
    try {
      const r = await fetch(
        `${API}/api/text_slots/${encodeURIComponent(row.slot_id)}/translate`,
        { method: "POST", credentials: "include" },
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const j = await r.json();
      // Splice the fresh values into the rows list so the UI updates in place.
      setRows((cur) => cur.map((cr) => (
        cr.slot_id === row.slot_id
          ? { ...cr, values: j.values || cr.values, updated_at: new Date().toISOString() }
          : cr
      )));
      // Drop any staged draft on the just-refreshed languages so the
      // operator doesn't see a "unsaved" chip on a cell that now
      // agrees with the server.
      setDrafts((cur) => {
        const d = cur[row.slot_id];
        if (!d) return cur;
        const nextRow = { ...d };
        for (const lang of (j.translated_langs || [])) delete nextRow[lang];
        return Object.keys(nextRow).length
          ? { ...cur, [row.slot_id]: nextRow }
          : (() => { const { [row.slot_id]: _, ...rest } = cur; return rest; })();
      });
      const filledLangs = (j.translated_langs || []).map((l) => l.toUpperCase()).join(", ");
      showToast("ok", filledLangs ? `Filled ${filledLangs} via Claude Haiku.` : "Already complete.");
    } catch (e) {
      showToast("error", e.message || "Translation failed");
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[row.slot_id]; return n; });
    }
  };

  // ── Bulk save ────────────────────────────────────────────────────────
  // Collects every pending edit across all rows on the page and fires
  // them off in parallel. Successful saves are merged into state and
  // their drafts cleared; failures surface in a single summary toast
  // so the operator knows exactly how many slots still need attention.
  const [bulkSaving, setBulkSaving] = useState(false);

  const dirtyCount = useMemo(() => {
    let n = 0;
    for (const r of rows) {
      const d = drafts[r.slot_id];
      if (!d) continue;
      for (const l of LANGS) {
        if (typeof d[l] === "string" && d[l] !== (r.values?.[l] || "")) { n++; break; }
      }
    }
    return n;
  }, [rows, drafts]);

  const saveAll = async () => {
    // Build the full list of PUT tasks (one per dirty language per slot).
    const tasks = [];
    const rowById = new Map(rows.map((r) => [r.slot_id, r]));
    for (const slotId of Object.keys(drafts)) {
      const row = rowById.get(slotId);
      if (!row) continue;
      const d = drafts[slotId] || {};
      for (const lang of LANGS) {
        if (typeof d[lang] === "string" && d[lang] !== (row.values?.[lang] || "")) {
          tasks.push({ slot_id: slotId, page: row.page || null, lang, text: d[lang] });
        }
      }
    }
    if (!tasks.length) return;

    setBulkSaving(true);
    setBusy((b) => {
      const next = { ...b };
      for (const t of tasks) next[t.slot_id] = true;
      return next;
    });

    const results = await Promise.allSettled(
      tasks.map((t) =>
        fetch(`${API}/api/text_slots/${encodeURIComponent(t.slot_id)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ values: { [t.lang]: t.text }, page: t.page }),
          cache: "no-store",
        }).then(async (r) => {
          if (!r.ok) throw new Error(`${t.slot_id} · HTTP ${r.status}`);
          const j = await r.json();
          return { slot_id: t.slot_id, values: j.values, updated_at: j.updated_at };
        })
      )
    );

    // Per-slot success aggregation: a slot is "fully saved" only when
    // every one of its tasks resolved. Partial failures keep the slot's
    // remaining drafts so the operator can retry.
    const okBySlot = new Map();   // slot_id → {values, updated_at, allOk: true}
    const failedSlots = new Set();
    tasks.forEach((t, i) => {
      const res = results[i];
      if (res.status === "fulfilled") {
        const cur = okBySlot.get(t.slot_id) || { values: null, updated_at: null, langs: new Set() };
        cur.values = res.value.values || cur.values;
        cur.updated_at = res.value.updated_at || cur.updated_at;
        cur.langs.add(t.lang);
        okBySlot.set(t.slot_id, cur);
      } else {
        failedSlots.add(t.slot_id);
      }
    });

    setRows((cur) => cur.map((r) => {
      const u = okBySlot.get(r.slot_id);
      return u ? { ...r, values: u.values || r.values, updated_at: u.updated_at || r.updated_at } : r;
    }));

    setDrafts((cur) => {
      const next = { ...cur };
      for (const [slotId, u] of okBySlot.entries()) {
        if (failedSlots.has(slotId)) {
          // Partial success — keep only the langs that failed.
          const remaining = { ...(next[slotId] || {}) };
          for (const lang of u.langs) delete remaining[lang];
          if (Object.keys(remaining).length === 0) delete next[slotId];
          else next[slotId] = remaining;
        } else {
          delete next[slotId];
        }
      }
      return next;
    });

    setBusy((b) => {
      const next = { ...b };
      for (const t of tasks) delete next[t.slot_id];
      return next;
    });
    setBulkSaving(false);

    const okCount = results.filter((r) => r.status === "fulfilled").length;
    const failCount = results.length - okCount;
    const fullySavedRows = okBySlot.size - failedSlots.size;
    const failedRows = failedSlots.size;
    if (failCount === 0) {
      showToast("ok", `Saved ${fullySavedRows} change${fullySavedRows === 1 ? "" : "s"} · auto-translating in the background…`);
    } else if (okCount === 0) {
      showToast("error", `Bulk save failed — ${failedRows} change${failedRows === 1 ? "" : "s"} could not be saved`);
    } else {
      showToast("error", `Saved ${fullySavedRows} · ${failedRows} still need attention — please retry the highlighted rows`);
    }

    // Pick up auto-translated siblings.
    window.setTimeout(load, 3000);
  };

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#2C2621] pb-24">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-[#FDFBF7] border-b border-[#2C2621]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-4 flex flex-wrap items-center gap-4">
          <Link to="/" className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542] transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.7} /> Site
          </Link>
          <div className="flex items-center gap-2.5">
            <Languages className="w-4 h-4 text-[#C16542]" strokeWidth={1.6} />
            <h1 className="font-serif-x text-2xl md:text-[26px] leading-none text-[#2C2621]">Content & translations</h1>
          </div>
          <span className="hidden sm:inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#5C5248] ml-auto">
            <FileText className="w-3 h-3" strokeWidth={1.7} />
            {rows.length} slot{rows.length !== 1 ? "s" : ""}
          </span>
          <button
            type="button"
            onClick={saveAll}
            disabled={!dirtyCount || bulkSaving}
            className={`inline-flex items-center gap-2 px-3.5 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors ${
              dirtyCount && !bulkSaving
                ? "bg-[#C16542] text-[#FDFBF7] hover:bg-[#A35133]"
                : "bg-[#2C2621]/15 text-[#2C2621]/50 cursor-not-allowed"
            }`}
            data-testid="admin-text-save-all-button"
            title={dirtyCount ? `Save every pending edit on this page (${dirtyCount})` : "No pending changes"}
          >
            {bulkSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" strokeWidth={1.8} />}
            {bulkSaving
              ? "Saving…"
              : dirtyCount
              ? `Save all changes · ${dirtyCount}`
              : "Save all changes"}
          </button>
          <button
            type="button"
            onClick={pullFromProduction}
            disabled={pulling || sync.running}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-[10px] tracking-[0.22em] uppercase border border-[#2C2621]/20 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="admin-text-pull-from-prod-button"
            title="Mirror every CMS collection from visitxaluca.com into this environment"
          >
            {pulling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudDownload className="w-3.5 h-3.5" strokeWidth={1.8} />}
            {pulling ? "Mirroring…" : "Mirror production DB"}
          </button>
          <button
            type="button"
            onClick={() => startBulkTranslate(null)}
            disabled={translate.running || sync.running}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-[10px] tracking-[0.22em] uppercase border border-[#2C2621]/20 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="admin-text-bulk-translate-button"
            title="Fill every empty ES / EN / FR value on every page using Claude Haiku. Never overwrites already-filled languages."
          >
            {translate.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" strokeWidth={1.8} />}
            {translate.running ? "Translating…" : "Translate in bulk"}
          </button>
          <button
            type="button"
            onClick={startSync}
            disabled={sync.running}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-[10px] tracking-[0.22em] uppercase bg-[#2C2621] text-[#FDFBF7] hover:bg-[#C16542] disabled:bg-[#2C2621]/30 disabled:cursor-not-allowed transition-colors"
            data-testid="admin-text-sync-button"
            title="Crawl the entire site in EN / FR / ES and register every visible text into the CMS"
          >
            {sync.running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} />}
            {sync.running ? "Scanning…" : "Re-scan website"}
          </button>
        </div>

        {/* Filter row */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 pb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5248]" strokeWidth={1.7} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by text, slot id or page…"
              className="w-full pl-9 pr-3 py-2.5 bg-white border border-[#2C2621]/15 text-sm focus:outline-none focus:border-[#C16542]"
              data-testid="admin-text-search"
            />
          </div>
          <button
            type="button"
            onClick={() => setActiveGroup("__all")}
            className={`text-[10px] tracking-[0.22em] uppercase px-3 py-2 border transition-colors ${activeGroup === "__all" ? "bg-[#2C2621] text-[#FDFBF7] border-[#2C2621]" : "border-[#2C2621]/15 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542]"}`}
            data-testid="admin-text-group-all"
          >
            All · {rows.length}
          </button>
          <select
            value={activeGroup}
            onChange={(e) => setActiveGroup(e.target.value)}
            className="text-[10px] tracking-[0.22em] uppercase px-3 py-2 bg-white border border-[#2C2621]/15 text-[#2C2621] focus:outline-none focus:border-[#C16542]"
            data-testid="admin-text-group-select"
          >
            <option value="__all">Filter by page…</option>
            {groups.map(([key, count]) => {
              const { label, emoji } = groupLabel(key);
              return (
                <option key={key} value={key}>{emoji} {label} · {count}</option>
              );
            })}
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-[10px] tracking-[0.22em] uppercase px-3 py-2 border border-[#2C2621]/15 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542] inline-flex items-center gap-2"
            data-testid="admin-text-reload"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" strokeWidth={1.7} />}
            Reload
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-[80px] right-6 z-50 px-4 py-3 border-l-4 bg-white shadow-lg text-sm flex items-center gap-2 ${
            toast.kind === "ok" ? "border-[#C16542] text-[#2C2621]" : "border-red-500 text-red-700"
          }`}
          data-testid="admin-text-toast"
        >
          {toast.kind === "ok" ? <CheckCircle2 className="w-4 h-4 text-[#C16542]" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
          {toast.msg}
        </div>
      )}

      {/* Live re-scan drawer */}
      {sync.visible && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[min(440px,92vw)] bg-[#2C2621] text-[#FDFBF7] border border-[#D4A373]/40 shadow-2xl"
          data-testid="admin-text-sync-drawer"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#FDFBF7]/15">
            {sync.running ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#D4A373]" strokeWidth={1.7} />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-[#D4A373]" strokeWidth={1.7} />
            )}
            <div className="text-[11px] tracking-[0.22em] uppercase flex-1">
              {sync.running ? "Re-scanning website" : "Sync finished"}
            </div>
            {!sync.running && (
              <button
                type="button"
                onClick={() => setSync((cur) => ({ ...cur, visible: false }))}
                className="text-[#FDFBF7]/70 hover:text-[#FDFBF7]"
                aria-label="Close sync drawer"
                data-testid="admin-text-sync-close"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            )}
          </div>
          <div className="px-4 py-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap max-h-[260px] overflow-y-auto bg-[#1A1513]/40">
            {(sync.tail || []).join("\n") || "Booting Chromium…"}
          </div>
          <div className="px-4 py-2 border-t border-[#FDFBF7]/15 text-[10px] tracking-[0.22em] uppercase text-[#FDFBF7]/70 flex items-center justify-between">
            <span>{sync.slotCount != null ? `${sync.slotCount.toLocaleString()} slots in DB` : ""}</span>
            <span>{sync.running ? "~3–5 min total" : sync.finishedAt ? "Done" : ""}</span>
          </div>
        </div>
      )}

      {/* Live bulk-translate drawer — appears when a bulk-translate job
          is running or has just finished. Independent from the re-scan
          drawer so both progress lanes can be visible at once (the
          re-scan drawer stacks above this one on the bottom-right). */}
      {translate.visible && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[min(440px,92vw)] bg-[#1A1513] text-[#FDFBF7] border border-[#D4A373]/40 shadow-2xl"
          style={{ transform: sync.visible ? "translateY(-320px)" : "none" }}
          data-testid="admin-text-translate-drawer"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#FDFBF7]/15">
            {translate.running ? (
              <Loader2 className="w-4 h-4 animate-spin text-[#D4A373]" strokeWidth={1.7} />
            ) : translate.error ? (
              <AlertCircle className="w-4 h-4 text-red-400" strokeWidth={1.7} />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-[#D4A373]" strokeWidth={1.7} />
            )}
            <div className="text-[11px] tracking-[0.22em] uppercase flex-1">
              {translate.running
                ? `Translating ${translate.page ? translate.page : "all pages"}`
                : translate.error
                ? "Translation failed"
                : "Translation finished"}
            </div>
            {!translate.running && (
              <button
                type="button"
                onClick={() => setTranslate((cur) => ({ ...cur, visible: false }))}
                className="text-[#FDFBF7]/70 hover:text-[#FDFBF7]"
                aria-label="Close translate drawer"
                data-testid="admin-text-translate-close"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            )}
          </div>
          <div className="px-4 py-3 text-[11px] leading-relaxed">
            {translate.error ? (
              <div className="text-red-300">{translate.error}</div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[#FDFBF7]/70 tracking-[0.18em] uppercase text-[10px]">Rows processed</span>
                  <span className="font-mono">{translate.done}/{translate.total || "?"}</span>
                </div>
                {/* Progress bar */}
                <div className="h-1.5 w-full bg-[#FDFBF7]/10">
                  <div
                    className="h-full bg-[#D4A373] transition-all"
                    style={{
                      width: translate.total > 0
                        ? `${Math.min(100, Math.round((translate.done / translate.total) * 100))}%`
                        : "0%",
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] tracking-[0.18em] uppercase text-[#FDFBF7]/70">
                  <span>Values filled: <span className="text-[#D4A373] font-mono normal-case tracking-normal">{translate.translated}</span></span>
                  {translate.failed > 0 && (
                    <span>Skipped: <span className="text-red-300 font-mono normal-case tracking-normal">{translate.failed}</span></span>
                  )}
                </div>
                {translate.currentSlot && translate.running && (
                  <div className="text-[10px] font-mono text-[#FDFBF7]/50 truncate" title={translate.currentSlot}>
                    · {translate.currentSlot.slice(-60)}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="px-4 py-2 border-t border-[#FDFBF7]/15 text-[10px] tracking-[0.22em] uppercase text-[#FDFBF7]/70 flex items-center justify-between">
            <span>Claude Haiku 4.5</span>
            <span>{translate.running ? "~2 s / value" : translate.finishedAt ? "Done" : ""}</span>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-8 pt-6">
        {error && (
          <div className="mb-6 px-4 py-3 border border-red-200 bg-red-50 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}
        {loading && !rows.length && (
          <div className="text-[#5C5248] text-sm py-12">
            <Loader2 className="w-4 h-4 inline-block animate-spin mr-2" /> Loading slots…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-[#5C5248] text-sm py-12">
            No content yet for this view. Edit any text inline on the site to create a slot,
            then it will appear here.
          </div>
        )}

        {/* When a query is active we ALWAYS show search results (across
            every page and language), regardless of the current group
            filter — that's the whole point of "global text search". The
            page-picker grid only appears when the operator is browsing
            with no query AND no specific group selected. Each result row
            already shows its source page via SlotRow's amber chip, so
            ambiguous matches (the same string used on multiple pages)
            are visually disambiguated without extra UI. */}
        {activeGroup === "__all" && !query.trim() ? (
          <div className="space-y-4">
            <p className="text-sm text-[#5C5248] mb-4">
              You have {rows.length.toLocaleString()} content slots across {groups.length} pages.
              Every route in the app is listed below — even pages that have never been scanned or
              edited yet appear here with 0 slots. Trigger a Re-scan to seed missing pages, or use
              the search box above to jump straight to any text.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {groups.map(([groupKey, count]) => {
                const { label, emoji } = groupLabel(groupKey);
                const isEmpty = count === 0;
                return (
                  <button
                    key={groupKey}
                    type="button"
                    onClick={() => setActiveGroup(groupKey)}
                    disabled={isEmpty}
                    className={`text-left bg-white border transition-colors px-4 py-3 flex items-center gap-3 ${
                      isEmpty
                        ? "border-dashed border-[#2C2621]/15 opacity-60 cursor-not-allowed"
                        : "border-[#2C2621]/12 hover:border-[#C16542]"
                    }`}
                    data-testid={`admin-text-pagecard-${groupKey}`}
                    title={isEmpty ? "Not scanned yet — press Re-scan website" : `Edit ${count} text slot${count === 1 ? "" : "s"} on ${groupKey}`}
                  >
                    <span className="text-2xl shrink-0">{emoji}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] text-[#2C2621] font-medium truncate">{label}</div>
                      <div className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] mt-0.5 truncate">
                        {isEmpty ? "Not scanned yet" : groupKey}
                      </div>
                    </div>
                    <span className={`text-[11px] tracking-[0.22em] uppercase tabular-nums ${isEmpty ? "text-[#5C5248]" : "text-[#C16542]"}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <>
            {query.trim() && activeGroup === "__all" && (
              <div className="mb-4 flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[#5C5248]" data-testid="admin-text-search-summary">
                <Search className="w-3 h-3" strokeWidth={1.7} />
                Showing {filtered.length.toLocaleString()} match{filtered.length === 1 ? "" : "es"} for
                <span className="text-[#C16542] normal-case tracking-normal font-medium">&ldquo;{query.trim()}&rdquo;</span>
                across all pages and translations.
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="ml-auto inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[#5C5248] hover:text-[#C16542]"
                  data-testid="admin-text-search-clear"
                >
                  <X className="w-3 h-3" strokeWidth={1.7} />
                  Clear
                </button>
              </div>
            )}
            {/* Per-page bulk-translate button — only when a real page is
                selected (not "__all", not the legacy catch-all buckets).
                Scoped to just this page's incomplete rows so the operator
                can quickly patch a single page without kicking off the
                full-site translate. */}
            {activeGroup !== "__all" && !activeGroup.startsWith("(") && (
              <div className="mb-4 flex items-center justify-between gap-3">
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#5C5248]" data-testid="admin-text-group-summary">
                  {filtered.length.toLocaleString()} slot{filtered.length === 1 ? "" : "s"} on <span className="text-[#C16542] font-medium normal-case tracking-normal">{activeGroup}</span>
                </div>
                <button
                  type="button"
                  onClick={() => startBulkTranslate(activeGroup)}
                  disabled={translate.running}
                  className="inline-flex items-center gap-2 px-3 py-2 text-[10px] tracking-[0.22em] uppercase border border-[#2C2621]/20 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  data-testid="admin-text-bulk-translate-page-button"
                  title={`Fill every empty ES / EN / FR value on ${activeGroup} using Claude Haiku. Never overwrites already-filled languages.`}
                >
                  {translate.running && translate.page === activeGroup
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Languages className="w-3.5 h-3.5" strokeWidth={1.8} />}
                  {translate.running && translate.page === activeGroup ? "Translating…" : "Translate this page in bulk"}
                </button>
              </div>
            )}
            <div className="space-y-5">
              {filtered.slice(0, visible).map((row) => (
                <SlotRow
                  key={row.slot_id}
                  row={row}
                  draft={draftOf(row)}
                  busy={!!busy[row.slot_id]}
                  dirty={isDirty(row)}
                  onChange={(lang, text) => setDraftField(row.slot_id, lang, text)}
                  onSave={() => saveRow(row)}
                  onReset={() => resetRow(row)}
                  onTranslate={() => translateRow(row)}
                />
              ))}
            </div>
            {filtered.length > visible && (
              <div className="mt-6 flex items-center justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="text-[10px] tracking-[0.22em] uppercase px-4 py-2 border border-[#2C2621]/20 text-[#2C2621] hover:border-[#C16542] hover:text-[#C16542]"
                  data-testid="admin-text-load-more"
                >
                  Load {Math.min(PAGE_SIZE, filtered.length - visible)} more · {visible} of {filtered.length} shown
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Single slot row — three-column side-by-side editor ─────────────────
const SlotRow = ({ row, draft, busy, dirty, onChange, onSave, onReset, onTranslate }) => {
  // A slot is "translate-able" whenever it has at least one populated
  // language — the server also flags rows where a field holds the wrong
  // language (crawler copy or human mis-paste), so relying only on
  // "some empty cell" would leave that class of row un-fixable from the
  // per-row button. If everything looks legitimately complete the
  // server responds with `translated_langs: []` and the UI just shows
  // a "Already complete" toast — no harm done.
  const filledLangs = LANGS.filter((l) => (row.values?.[l] || "").trim());
  const emptyLangs = LANGS.filter((l) => !(row.values?.[l] || "").trim());
  const canTranslate = filledLangs.length > 0;
  const translateLabel = emptyLangs.length > 0
    ? `Fill ${emptyLangs.map((l) => l.toUpperCase()).join(" + ")} (and fix any wrong-language cells)`
    : canTranslate
    ? "Recheck & fix any wrong-language cells"
    : "Nothing to translate from";
  return (
    <article
      className={`bg-white border ${dirty ? "border-[#C16542]" : "border-[#2C2621]/12"} shadow-[0_1px_0_rgba(28,22,19,0.04)] transition-colors`}
      data-testid={`admin-text-row-${row.slot_id}`}
    >
      <header className="flex flex-wrap items-center gap-3 px-4 md:px-5 py-3 border-b border-[#2C2621]/8 bg-[#FDFBF7]/60">
        <code className="text-[11px] text-[#5C5248] truncate max-w-full" title={row.slot_id}>
          {row.slot_id}
        </code>
        {row.page && (
          <span className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.22em] uppercase text-[#C16542] border border-[#C16542]/40 px-2 py-0.5">
            <Globe2 className="w-2.5 h-2.5" strokeWidth={1.7} />
            {row.page}
          </span>
        )}
        {row.source_lang && (
          <span className="text-[9px] tracking-[0.22em] uppercase text-[#5C5248]">
            source · {LANG_FLAG[row.source_lang] || row.source_lang}
          </span>
        )}
        <span className="text-[10px] text-[#5C5248] ml-auto tabular-nums">
          {fmtDate(row.updated_at)}
        </span>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#2C2621]/8">
        {LANGS.map((lang) => {
          const value = draft[lang] !== undefined ? draft[lang] : (row.values?.[lang] || "");
          const original = row.values?.[lang] || "";
          const langDirty = draft[lang] !== undefined && draft[lang] !== original;
          return (
            <div key={lang} className="p-3.5">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-7 h-5 bg-[#2C2621] text-[#FDFBF7] text-[9px] tracking-[0.22em]">
                    {LANG_FLAG[lang]}
                  </span>
                  <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
                    {LANG_LABEL[lang]}
                  </span>
                </div>
                {langDirty && (
                  <span className="text-[9px] tracking-[0.22em] uppercase text-[#C16542]">unsaved</span>
                )}
              </div>
              <textarea
                value={value}
                onChange={(e) => onChange(lang, e.target.value)}
                rows={Math.max(3, Math.min(10, Math.ceil((value?.length || 0) / 60)))}
                className="w-full px-2.5 py-2 bg-[#FDFBF7] border border-[#2C2621]/15 text-[13px] leading-relaxed font-sans text-[#2C2621] focus:outline-none focus:border-[#C16542] resize-y"
                placeholder={`${LANG_LABEL[lang]} translation…`}
                data-testid={`admin-text-input-${row.slot_id}-${lang}`}
              />
            </div>
          );
        })}
      </div>

      <footer className="px-4 md:px-5 py-3 border-t border-[#2C2621]/8 bg-[#FDFBF7]/50 flex flex-wrap items-center gap-2">
        <span className="text-[10px] text-[#5C5248]">
          Edit any field and click <strong className="text-[#2C2621]">Save</strong> — the other languages
          auto-translate via Claude in the background.
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={!dirty || busy}
            className="text-[10px] tracking-[0.22em] uppercase px-3 py-2 border border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid={`admin-text-reset-${row.slot_id}`}
          >
            <RotateCcw className="w-3 h-3" strokeWidth={1.7} />
            Reset
          </button>
          <button
            type="button"
            onClick={onTranslate}
            disabled={!canTranslate || busy}
            className="text-[10px] tracking-[0.22em] uppercase px-3 py-2 border border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
            data-testid={`admin-text-translate-${row.slot_id}`}
            title={translateLabel}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" strokeWidth={1.7} />}
            Translate
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!dirty || busy}
            className="text-[10px] tracking-[0.22em] uppercase px-3 py-2 bg-[#2C2621] text-[#FDFBF7] disabled:bg-[#2C2621]/25 disabled:cursor-not-allowed hover:bg-[#C16542] inline-flex items-center gap-1.5"
            data-testid={`admin-text-save-${row.slot_id}`}
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" strokeWidth={1.8} />}
            Save
          </button>
        </div>
      </footer>
    </article>
  );
};

// Auth wrapper
const AdminTextPage = () => (
  <AdminAuthGate>
    <AdminTextInner />
  </AdminAuthGate>
);

export default AdminTextPage;
